import { FC } from 'react';
import styled, { css } from 'styled-components';
import { useColorTheme } from './useColorTheme';
import { useThemeToggle } from './useThemeToggle';

const ICON_SIZE = css`
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PRESSED_SEGMENT_SHADOW = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';

/** `menu` sits on the always-white profile popover; `card` on a themed surface. */
type tThemeSegmentsVariant = 'menu' | 'card';

interface IThemeSegments {
  /** The id of the visible label naming the group. */
  labelledBy: string;
  variant?: tThemeSegmentsVariant;
}

/** Light / Dark / Automatic, one pressed: the student's saved choice. */
export const ThemeSegments: FC<IThemeSegments> = ({ labelledBy, variant = 'menu' }) => {
  const { preference, switchTheme } = useColorTheme();
  const { options } = useThemeToggle();

  return (
    <SSegments role="group" aria-labelledby={labelledBy} $variant={variant}>
      {options.map(({ value, label, Icon }) => (
        <SSegmentButton
          key={value}
          type="button"
          aria-pressed={preference === value}
          aria-label={label}
          onClick={() => switchTheme(value)}
        >
          <SSegmentIcon>
            <Icon />
          </SSegmentIcon>
        </SSegmentButton>
      ))}
    </SSegments>
  );
};

const SSegmentButton = styled.button`
  display: flex;
  padding: 6px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: var(--color-black);
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background-color: rgb(255 255 255 / 0.6);
  }

  &[aria-pressed='true'] {
    background-color: var(--color-white);
    box-shadow: ${PRESSED_SEGMENT_SHADOW};
  }

  &:focus-visible {
    outline: 2px solid var(--color-blue-btn-focus-outline-color);
    outline-offset: -2px;
  }
`;

const SSegmentIcon = styled.span`
  display: flex;
  ${ICON_SIZE}
`;

const CARD_VARIANT = css`
  background-color: rgb(var(--ln-surface-sunken));

  ${SSegmentButton} {
    color: rgb(var(--ln-content));

    &:hover {
      background-color: rgb(var(--ln-surface-control) / 0.6);
    }

    &[aria-pressed='true'] {
      background-color: rgb(var(--ln-surface-control));
    }
  }
`;

const SSegments = styled.div<{ $variant: tThemeSegmentsVariant }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
  margin-inline-start: auto;
  padding: 2px;
  border-radius: 8px;
  background-color: var(--ln-menu-hover);

  ${({ $variant }) => $variant === 'card' && CARD_VARIANT}
`;
